import { Box } from "@dropbox/dig-foundations";
import { getBodyConfig } from "../util/config";
import { FormattedText } from "./FormattedText";
import { ResultAnnotations } from "./ResultAnnotations";
import { SnippetText } from "./SnippetText";

import type { SearchResult } from "@mirage/service-dbx-api";
import { useFeatureFlagValueBool } from "@mirage/service-experimentation/useFeatureFlagValue";
import type { BodyField } from "../util/config";

type ResultBodyProps = {
  result: SearchResult;
  query: string;
  bodyFields: BodyField[];
  resultPosition: number;
  annotationsEnabled?: boolean;
};

export const ResultBody = ({
  result,
  query,
  bodyFields,
  resultPosition,
  annotationsEnabled,
}: ResultBodyProps) => {
  const isServerDrivenHighlightingOnlyEnabled = useFeatureFlagValueBool(
    "dash_2025_04_17_server_driven_highlighting_only",
  );
  const sanitizeMarkdownEnabled = useFeatureFlagValueBool(
    "dash_2025_04_24_truncate_markdown_snippets",
  );
  const pullChatHighlightFromMessage = useFeatureFlagValueBool(
    "dash_2025_04_27_pull_chat_highlight_from_message",
  );
  const useFullMarkdownForMessageType = useFeatureFlagValueBool(
    "dash_2025_04_28_use_full_markdown_for_message_type",
  );

  const isMessageType =
    result.logicalType === "message" || result.logicalType === "thread";

  return (
    <Box as="div">
      <Box as="div">
        {bodyFields.map((bodyField: BodyField) => {
          const bodyFieldProps = getBodyConfig(
            bodyField,
            result,
            query,
            pullChatHighlightFromMessage,
          );
          if (isServerDrivenHighlightingOnlyEnabled || isMessageType) {
            return (
              <FormattedText
                key={bodyField}
                {...bodyFieldProps}
                sanitizeMarkdown={
                  isMessageType && useFullMarkdownForMessageType
                    ? false
                    : sanitizeMarkdownEnabled
                }
                removeBoldText={isMessageType}
              />
            );
          }
          if (bodyFieldProps && bodyFieldProps[".tag"] === "snippet") {
            return <SnippetText key={bodyField} {...bodyFieldProps} />;
          }
          return null;
        })}
      </Box>
      {annotationsEnabled && (
        <ResultAnnotations
          result={result}
          resultPosition={resultPosition}
          query={query}
        />
      )}
    </Box>
  );
};
